import {createStore} from "vuex";
import VuexPersistence from "vuex-persist";
import {getCleanVisibleLayers, getSSID} from "@/js/utils";
import localforage from 'localforage';
import {cloneDeep, debounce} from 'lodash';
import {db} from '@/js/store/offline/database';
import {prepareForSave, prepareForUse} from "@/js/store/offline/data";
import {v4 as uuid} from "uuid";
import {Directory, Filesystem} from '@capacitor/filesystem';
import {config} from "@/config/config";
import {Capacitor} from '@capacitor/core';
import {checkStoragePermissions, deleteDocumentFolder, getDocumentById, getDocumentPath, getDocumentTypeById, getDocumentZip, getFilenameWithoutExtension, imageToBase64, importDocumentFromFolder, renameDocumentFolder} from "@/js/store/offline/utilities";
import {Share} from "@capacitor/share";
import {Zip} from "@capawesome-team/capacitor-zip";
import {FileCompressor} from '@capawesome-team/capacitor-file-compressor';

let urlParams = typeof window !== "undefined" ? new URL(document.location).searchParams : new URLSearchParams();
let ssid = getSSID();

const localstoragePlugin = new VuexPersistence({
  key: 'vuex',
  storage: typeof window !== "undefined" ? window.localStorage : null,
  reducer: (state) => {
    let obj = cloneDeep(state);
    obj[ssid] = {
      user: obj[ssid].user,
      token: obj[ssid].token,
      iframe: obj[ssid].iframe,
      drawingTools: {
        ...obj[ssid].drawingTools,
        finishEditFeature: false,
        featureUpdated: false,
        activeHorizontalButton: "",
        startExtent: false,
        startPointExtent: null,
        stopPointExtent: null,
      },
      mapSettings: obj[ssid].mapSettings || {
        centerIcon: "icon_map_center_default.svg",
        centerColor: "#000000",
      },
      mapState: {
        mode: undefined,
        drawMode: undefined,
        drawAction: undefined,
        selectedDate: undefined,
        ...obj[ssid].mapState,
      },
      selectedOrganizationId: obj[ssid].selectedOrganizationId,
      selectedEmployeeId: obj[ssid].selectedEmployeeId,
      projectionConversion: null,
      formTemplatesParams: obj[ssid].formTemplatesParams,
    };
    return obj;
  }
})

const throwError = (error, dispatch, resolve, reject) => {
  if (/428|498/.test(error?.response?.status)) {
    reject(error);
  } else if (/401|403/.test(error?.response?.status)) {
    reject(
      error.response?.data?.details?.message ||
      error.response?.data?.details ||
      error.response?.data?.message ||
      error?.response?.data ||
      error?.message ||
      error
    );
  } else
    reject(
      error.response?.data?.details?.message ||
      error.response?.data?.details ||
      error.response?.data?.message ||
      error?.response?.data ||
      error?.message ||
      error
    );
};

const debouncedSaveState = debounce(async (key, state) => {
  try {
    await localforage.setItem(key, state);
  } catch (error) {
    console.error("Failed to save state", error);
  }
}, 500);

const localforagePlugin = new VuexPersistence({
  key: 'vuex',
  storage: localforage,
  asyncStorage: true,
  restoreState: async (key, storage) => {
    try {
      const state = await localforage.getItem('vuex');
      return state || {};
    } catch (err) {
      console.error('Failed to restore state from IndexedDB', err);
      return {};
    }
  },
  saveState: async (key, state, storage) => {
    try {
      debouncedSaveState(key, state);
    } catch (err) {
      console.error('Failed to save state to IndexedDB', err);
    }
  },
  reducer: (state) => {
    let obj = cloneDeep(state);
    obj[ssid] = {
      common: obj[ssid].common,
      currentProject: {
        ...obj[ssid].currentProject,
        visibleLayers: obj[ssid]?.currentProject?.visibleLayers
          ? getCleanVisibleLayers(obj[ssid].currentProject.visibleLayers)
          : [],
        projects: null,
        search: null,
      },
      currentGlobalComponents: obj[ssid].currentGlobalComponents,
      currentStages: obj[ssid].currentStages,
      currentForms: obj[ssid].currentForms,
    };
    return obj;
  },
});

const store = createStore({
  state: {},
  mutations: {
    SET_COMMON_DATA(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].common = data;
      console.log(new Blob([JSON.stringify(state[ssid].common)]).size / 1024)
    },

    SET_CURRENT_PROJECT(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (data && data.layers) {
        state[ssid].currentProjectLayers = data.layers;
        delete data.layers;
      }
      if (data && data.visibleLayers) {
        data.visibleLayers = getCleanVisibleLayers(data.visibleLayers);
      }
      state[ssid].currentProject = data;
    },
    SET_UPLOAD_WORKER(state, data) {
      console.log("SET_UPLOAD_WORKER", state, data, ssid);
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].uploadWorker = data;
    },
    SET_UPLOAD_FILE(state, data) {
      ssid = ssid ? ssid : data.ssid;
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].uploadFile = data;
    },
    SET_USER(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].user = data ? {
        ...state[ssid].user,
        ...data
      } : data;
    },
    SET_TOKEN(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].token = data;
    },
    SET_SELECTED_ORGANIZATION_ID(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].selectedOrganizationId = data;
    },
    SET_SELECTED_EMPLOYEE_ID(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].selectedEmployeeId = data;
    },
    SET_FORM_TEMPLATES_PARAMS(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].formTemplatesParams = data;
    },
    SET_IFRAME(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].iframe = data;
    },

    //pure function for mapBoxDraw
    SET_MAP_STATE(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].mapState = data;
    },
    SET_MAP_SETTINGS(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].mapSettings = data;
    },
    SET_FINISH_EDIT_FEATURE(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.finishEditFeature = data;
    },
    SET_FEATURE_UPDATED(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.featureUpdated = data;
    },
    SET_ACTIVE_HORIZONTAL_BUTTON(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.activeHorizontalButton = data;
    },
    SET_ACTIVE_CENTER_TOOLBOX_BUTTON(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.topCenterEditToolBarActiveButtons = data;
    },
    SET_START_EXTENT(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.startExtent = data;
    },
    SET_START_POINT_EXTENT(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.startPointExtent = data;
    },
    SET_STOP_POINT_EXTENT(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      if (!state[ssid].drawingTools) state[ssid].drawingTools = {};
      state[ssid].drawingTools.stopPointExtent = data;
    },
    SET_PROJECTION_CONVERSION(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].projectionConversion = data;
    },
    SET_CURRENT_GLOBAL_COMPONENTS(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].currentGlobalComponents = data;
    },
    SET_CURRENT_STAGES(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].currentStages = data;
    },
    SET_CURRENT_FORMS(state, data) {
      if (!state.hasOwnProperty(ssid)) state[ssid] = {};
      state[ssid].currentForms = data;
    }
  },
  getters: {
    GET_COMMON_DATA(state) {
      return state[ssid] && state[ssid].common ? state[ssid].common : {};
    },
    GET_SSID(state) {
      return ssid;
    },
    GET_CURRENT_PROJECT(state) {
      return state[ssid] && state[ssid].currentProject
        ? {
          ...state[ssid].currentProject,
          layers: state[ssid].currentProjectLayers,
        }
        : null;
    },
    GET_USER(state) {
      return state[ssid] && state[ssid].user ? state[ssid].user : null;
    },
    GET_UPLOAD_WORKER(state) {
      return state[ssid] && state[ssid].uploadWorker ? state[ssid].uploadWorker : null;
    },
    GET_UPLOAD_FILE(state) {
      return state[ssid] && state[ssid].uploadFile ? state[ssid].uploadFile : null;
    },
    GET_TOKEN(state) {
      return state[ssid] && state[ssid].token ? state[ssid].token : null;
    },
    GET_SELECTED_ORGANIZATION_ID(state) {
      return state[ssid] && state[ssid].selectedOrganizationId ? state[ssid].selectedOrganizationId : null;
    },
    GET_SELECTED_EMPLOYEE_ID(state) {
      return state[ssid] && state[ssid].selectedEmployeeId ? state[ssid].selectedEmployeeId : null;
    },
    GET_FORM_TEMPLATES_PARAMS(state) {
      return state[ssid] && state[ssid].formTemplatesParams ? state[ssid].formTemplatesParams : null;
    },
    GET_IFRAME(state) {
      return state[ssid] && state[ssid].iframe ? state[ssid].iframe : null;
    },
    GET_MAP_BOX_DRAW(state) {
      return state[ssid] && state[ssid].drawingTools ? state[ssid].drawingTools : null;
    },
    GET_STATE(state) {
      return state[ssid];
    },

    //getter fro mapBoxDraw
    GET_MAP_STATE(state) {
      return state[ssid] && state[ssid]?.mapState;
    },
    GET_MAP_SETTINGS(state) {
      return (state[ssid] && state[ssid]?.mapSettings) || {
        centerIcon: "icon_map_center_default.svg",
        centerColor: "#000000",
      };
    },
    GET_FINISH_EDIT_FEATURE(state) {
      return state[ssid] && state[ssid]?.drawingTools?.finishEditFeature
        ? state[ssid].drawingTools.finishEditFeature
        : false;
    },
    GET_FEATURE_UPDATED(state) {
      return state[ssid] && state[ssid]?.drawingTools?.featureUpdated ? state[ssid].drawingTools.featureUpdated : false;
    },
    GET_ACTIVE_HORIZONTAL_BUTTON(state) {
      return state[ssid] && state[ssid]?.drawingTools?.activeHorizontalButton
        ? state[ssid].drawingTools.activeHorizontalButton
        : null;
    },
    GET_START_EXTENT(state) {
      return state[ssid] && state[ssid]?.drawingTools?.startExtent ? state[ssid].drawingTools.startExtent : false;
    },
    GET_START_POINT_EXTENT(state) {
      return state[ssid] && state[ssid]?.drawingTools?.startPointExtent
        ? state[ssid].drawingTools.startPointExtent
        : null;
    },
    GET_STOP_POINT_EXTENT(state) {
      return state[ssid] && state[ssid]?.drawingTools?.stopPointExtent
        ? state[ssid].drawingTools.stopPointExtent
        : null;
    },
    GET_PROJECTION_CONVERSION(state) {
      return state[ssid] && state[ssid]?.projectionConversion ? state[ssid].projectionConversion : null;
    },
    GET_CURRENT_GLOBAL_COMPONENTS(state) {
      return state[ssid] && state[ssid]?.currentGlobalComponents ? state[ssid].currentGlobalComponents : [];
    },
    GET_CURRENT_STAGES(state) {
      return state[ssid] && state[ssid]?.currentStages ? state[ssid].currentStages : {};
    },
    GET_CURRENT_FORMS(state) {
      return state[ssid] && state[ssid]?.currentForms ? state[ssid].currentForms : {};
    }
  },
  actions: {
    setCommonData({commit}, data) {
      commit("SET_COMMON_DATA", data);
    },
    setMapSettings({commit}, data) {
      commit("SET_MAP_SETTINGS", data);
    },
    setIframe({commit}, data) {
      commit("SET_IFRAME", data);
    },
    setSelectedOrganizationId({commit}, data) {
      commit("SET_SELECTED_ORGANIZATION_ID", data);
    },
    setSelectedEmployeeId({commit}, data) {
      commit("SET_SELECTED_EMPLOYEE_ID", data);
    },
    setFormTemplatesParams({commit}, data) {
      commit("SET_FORM_TEMPLATES_PARAMS", data);
    },
    setCurrentProject({commit}, data) {
      commit("SET_CURRENT_PROJECT", data);
    },
    setCurrentGlobalComponents({commit}, data) {
      commit("SET_CURRENT_GLOBAL_COMPONENTS", data);
    },
    setCurrentStages({commit}, data) {
      commit("SET_CURRENT_STAGES", data);
    },
    setCurrentForms({commit}, data) {
      commit("SET_CURRENT_FORMS", data);
    },
    setUser({commit}, data) {
      commit("SET_USER", data);
    },
    setUploadWorker({commit}, data) {
      commit("SET_UPLOAD_WORKER", data);
    },
    setUploadFile({commit}, data) {
      commit("SET_UPLOAD_FILE", data);
    },

    getUser: ({commit, dispatch, state}, data) => {
      console.log('getUser', data);
      return new Promise((resolve, reject) => {
        resolve(state[ssid].user);
      });
    },

    getUserCompanies: ({dispatch, state, commit}, data) => {
      console.log('getUserCompanies', data);
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getProjectsAndLayers: ({dispatch, state}, data) => {
      console.log('getProjectsAndLayers', data);
      return new Promise(async (resolve, reject) => {
        let response = [];
        await db.documents
          //.where("documentTypeId").anyOf([40])
          .toArray()
          .then(result => {
            response = prepareForUse(result);
          }).catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });

        let result = [];
        for (let item of response) {
          if(!item.parentDocumentId){
            result.push(item);
          }
        }

        for (let item of result) {
          // get children
          await db.documents
            .filter(child => child.parentDocumentId === item.documentId)
            .toArray()
            .then(async result => {
              item.children = prepareForUse(result)
              for (let child of item.children) {
                await db.documents
                  .filter(el => el.parentDocumentId === child.documentId)
                  .toArray()
                  .then(result => {
                    child.children = prepareForUse(result)
                  });
              }
            });
        }
        resolve(result)
      });
    },

    getCompanies: ({dispatch, state, commit}, data) => {
      console.log('getCompanies', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getAttributes: ({dispatch, state}, data) => {
      console.log('getAttributes', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getUserOrganization: ({dispatch, state, commit}, data) => {
      console.log('getUserOrganization', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getRoles: ({dispatch, state, commit}, data) => {
      console.log('getRoles', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getModules: ({dispatch, state, commit}, data) => {
      console.log('getModules', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getVirtualTours: ({commit, dispatch, state}, data) => {
      console.log('getVirtualTours', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getRightsList: ({dispatch, state, commit}, data) => {
      console.log('getRightsList', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getAllDocumentTypes: ({dispatch, state, commit}, data) => {
      console.log('getAllDocumentTypes', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getDocumentCategories: ({dispatch, state, commit}, data) => {
      console.log('getDocumentCategories', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getProjections: ({dispatch, state}, data) => {
      console.log('getProjections', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    signOut: ({commit, state, dispatch}, data) => {
      return new Promise((resolve, reject) => {
        commit("SET_USER", null);
        commit("SET_TOKEN", null);
        commit("SET_CURRENT_PROJECT", null);
        commit("SET_IFRAME", null);
        commit("SET_SELECTED_ORGANIZATION_ID", null);
        commit("SET_SELECTED_EMPLOYEE_ID", null);
        localStorage.setItem('appMode', 'default');
        resolve();
      });
    },

    getStyle: ({commit, dispatch, state}, data) => {
      console.log('getStyle', data)
      return new Promise(async (resolve, reject) => {
        reject('Not available in offline mode!');
      });
    },

    googleGeocodeSearch: ({dispatch, state}, data) => {
      console.log('googleGeocodeSearch', data)
      return new Promise(async (resolve, reject) => {
        reject('Not available in offline mode!');
      });
    },

    getGeometryDistinctDates: ({dispatch, state}, data) => {
      console.log('getGeometryDistinctDates', data)
      return new Promise(async (resolve, reject) => {
        reject('Not available in offline mode!');
      });
    },

    getUserDepartments: ({dispatch, state}, data) => {
      console.log('getUserDepartments', data)
      return new Promise(async (resolve, reject) => {
        await db.organizations.count().then(async result => {
          if (result < 1) {
            await db.organizations.add({
              title: config.APP_ORGANIZATION_NAME || 'Default',
              organizationId: config.APP_ORGANIZATION_ID || null,
              parentOrganizationId: null,
              depth: 0
            })
          }
        })
        resolve(await db.organizations.toArray());
      });
    },

    getStatuses: ({state, dispatch, commit}, data) => {
      console.log('getStatuses', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getOrganizationCategories: ({dispatch, state}, data) => {
      console.log('getOrganizationCategories', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getOrganizationTypes: ({dispatch, state}, data) => {
      console.log('getOrganizationTypes', data)
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getConnections: ({commit, dispatch, state}, data) => {
      console.log('getConnections', data)
      return new Promise(async (resolve, reject) => {
        reject('Not available in offline mode!');
      });
    },

    getMBTilesFolders: ({commit, dispatch, state}) => {
      console.log('getMBTilesFolders')
      return new Promise(async (resolve, reject) => {
        reject('Not available in offline mode!');
      });
    },

    uploadFile: ({dispatch, state}, data) => {
      console.log('uploadFile', data)
      return new Promise(async (resolve, reject) => {
        await checkStoragePermissions();

        const reader = new FileReader();
        const file = data.data.file.get('file');
        const fileName = new Date().getTime() + '_' + file.name;
        const filePath = `${config.APP_MOBILE_FOLDER}/${await getDocumentPath(data.data.params.documentId)}/${data?.data?.params?.isMarker ? 'Markers' : 'Attachments'}/${fileName}`;

        reader.readAsDataURL(file);
        reader.onload = async () => {
          await Filesystem.writeFile({
            path: filePath,
            data: reader.result,
            directory: Directory.Documents,
            recursive: true,
          });
          let localFile = await Filesystem.getUri({
            path: filePath,
            directory: Directory.Documents,
          });

          if (/jpg|jpeg|png|webp|gif/gi.test(fileName)) {
            try {
              const compressedPath = await FileCompressor.compressImage({
                mimeType: 'image/jpeg',
                path: localFile.uri,
                quality: 0.3,
              });

              await Filesystem.copy({
                from: compressedPath.path,
                to: localFile.uri
              });

              await Filesystem.deleteFile({
                path: compressedPath.path
              })
            } catch (e) {
              console.log(e);
            }
          }

          resolve({
            url: Capacitor.convertFileSrc(localFile.uri)
          });
        };
        reader.onerror = (error) => {
          reject(error);
        };
      });
    },

    uploadSignedFile: ({dispatch, state}, data) => {
      console.log('uploadSignedFile', data)
      return new Promise(async (resolve, reject) => {
        reject('Not available in offline mode!');
      });
    },

    addProject: ({dispatch, state}, data) => {
      console.log('addProject', data);
      return new Promise((resolve, reject) => {
        data.data.documentCategory = {
          title: "Project",
          documentCategoryId: 2,
        }
        db.documents.add({
          ...prepareForSave(data.data),
          documentTypeId: data.data?.documentType?.documentTypeId,
          documentCategoryId: 2,
        }).then(async result => {
          resolve(prepareForUse(await db.documents.get(result)));
        }).catch((error) => {
          throwError(error, dispatch, resolve, reject);
        });
      });
    },

    updateProject: ({dispatch, state}, data) => {
      console.log('updateProject', data);
      return new Promise(async (resolve, reject) => {
        const oldDocument = await getDocumentById(data.data.documentId);
        let oldFolder;
        if (oldDocument.title !== data.data.title) {
          oldFolder = await getDocumentPath(oldDocument.documentId);
        }

        db.documents.update(data.data.documentId, {
          ...prepareForSave(data.data),
          documentTypeId: data.data?.documentType?.documentTypeId,
          documentCategoryId: 2,
        }).then(async result => {
          if (oldFolder) {
            const newFolder = await getDocumentPath(data.data.documentId);
            await renameDocumentFolder(oldFolder, newFolder);
          }
          resolve(prepareForUse(await db.documents.get(data.data.documentId)));
        }).catch((error) => {
          throwError(error, dispatch, resolve, reject);
        });
      });
    },

    deleteProject: ({dispatch, state}, data) => {
      console.log('deleteProject', data);
      return new Promise(async (resolve, reject) => {
        db.documents.where('parentDocumentId').anyOf([data.data.documentId]).delete();
        await deleteDocumentFolder(data.data.documentId);
        db.documents.delete(data.data.documentId).then(result => {
          resolve({message: 'Proiectul a fost sters.'});
        }).catch((error) => {
          throwError(error, dispatch, resolve, reject);
        });
      });
    },

    getFullProject: ({commit, dispatch, state}, data) => {
      console.log('getFullProject', data)
      return new Promise(async (resolve, reject) => {
        resolve({
          document: prepareForUse(await db.documents.get(data.data.parentDocumentId)),
          documentChildren: prepareForUse(await db.documents.where('parentDocumentId').equals(data.data.parentDocumentId).toArray()),
        });
      });
    },

    getIntegrations: ({dispatch, state}, data) => {
      console.log('getIntegrations', data);
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    getOrganizationDescendants: ({dispatch, state}, data) => {
      console.log('getOrganizationDescendants', data);
      return new Promise(async (resolve, reject) => {
        await db.organizations.count().then(async result => {
          if (result < 1) {
            await db.organizations.add({
              title: 'Offline organization',
              parentOrganizationId: null,
              depth: 0
            })
          }
        })
        resolve(await db.organizations.toArray());
      });
    },

    addLayer: ({dispatch, state}, data) => {
      console.log('addLayer', data);
      return new Promise((resolve, reject) => {
        db.documents.add({
          ...prepareForSave(data.data),
          documentTypeId: data.data?.documentType?.documentTypeId,
          documentCategoryId: data.data?.documentCategory?.documentCategoryId,
        }).then(async result => {
          resolve(prepareForUse(await db.documents.get(result)));
        }).catch((error) => {
          throwError(error, dispatch, resolve, reject);
        });
      });
    },

    editLayer: ({dispatch, state}, data) => {
      console.log('editLayer', data);
      return new Promise(async (resolve, reject) => {
        const oldDocument = await getDocumentById(data.data.documentId);
        let oldFolder;
        if (oldDocument.title !== data.data.title) {
          oldFolder = await getDocumentPath(oldDocument.documentId);
        }

        db.documents.update(data.data.documentId, {
          ...prepareForSave(data.data),
          documentTypeId: data.data?.documentType?.documentTypeId,
          documentCategoryId: data.data?.documentCategory?.documentCategoryId,
        }).then(async result => {
          if (oldFolder) {
            const newFolder = await getDocumentPath(data.data.documentId);
            await renameDocumentFolder(oldFolder, newFolder);
          }
          resolve(prepareForUse(await db.documents.get(data.data.documentId)));
        }).catch((error) => {
          throwError(error, dispatch, resolve, reject);
        });
      });
    },

    deleteLayer: ({dispatch, state}, data) => {
      console.log('deleteLayer', data);
      return new Promise(async (resolve, reject) => {
        db.geometries.where('documentId').anyOf([data.data.documentId]).delete();
        await deleteDocumentFolder(data.data.documentId);
        db.documents.delete(data.data.documentId).then(result => {
          resolve({message: "Stratul a fost sters."});
        }).catch((error) => {
          throwError(error, dispatch, resolve, reject);
        });
      });
    },

    getGeometries: ({dispatch, state}, data) => {
      console.log('getGeometries', data);
      return new Promise(async (resolve, reject) => {
        let geometriesIds = [];
        if (data.data.geometries) {
          for (let g of data.data.geometries) {
            geometriesIds.push(g.geometryId);
          }
        }
        db.geometries
          .filter(geom => geometriesIds.includes(geom.geometryId))
          .toArray(result => {
            resolve(prepareForUse(result));
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    getLayerGeometries: ({commit, dispatch, state}, data) => {
      console.log('getLayerGeometries', data);
      return new Promise(async (resolve, reject) => {
        db.geometries
          .filter(geom => geom.documentId === data.query.documentId)
          .toArray(result => {
            resolve(prepareForUse(result));
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    saveBulkGeometries: ({dispatch, state}, data) => {
      console.log('saveBulkGeometries', data);
      return new Promise(async (resolve, reject) => {
        db.geometries
          .bulkPut(prepareForSave(data.data), {allKeys: true})
          .then(async result => {
            resolve(prepareForUse(await db.geometries.filter(el => result.includes(el.geometryId)).toArray()).map(x => ({
              ...x,
              ...x.geometry,
              documentId: x.documentId,
              geometryId: x.geometryId || x.id || uuid()
            })));
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    },

    getDocumentDetails: ({commit, dispatch, state}, data) => {
      console.log('getDocumentDetails', data);
      return new Promise(async (resolve, reject) => {
        resolve(prepareForUse(await db.documents.get(data.data.documentId)));
      });
    },

    getGeometryId: ({commit, dispatch, state}, data) => {
      console.log('getGeometryId', data);
      return new Promise(async (resolve, reject) => {
        resolve(prepareForUse(await db.geometries.get(data.data.geometryId)));
      });
    },

    getSharesUser: ({commit, dispatch, state}, data) => {
      console.log('getSharesUser', data)
      return new Promise(async (resolve, reject) => {
        reject('Not available in offline mode!');
      });
    },

    exportToZip: ({commit, dispatch, state}, data) => {
      console.log('exportToZip', data)
      return new Promise(async (resolve, reject) => {
        const exportFile = await getDocumentZip(data.data.documentId);
        await Share.share({
          files: [exportFile.uri]
        }).catch(error => {
          console.log(error);
          resolve();
        });

        // cleanup: delete export file
        try {
          await Filesystem.deleteFile({
            path: exportFile.path,
            directory: exportFile.directory,
          });
        } catch (e) {
          console.log(e);
        }
        resolve();
      });
    },

    exportLayer: async ({commit, dispatch, state}, data) => {
      console.log('exportLayer', data)
      await dispatch('exportToZip', data);
    },

    importLayerGeometries: async ({commit, dispatch, state}, data) => {
      console.log('importLayerGeometries', data)
      await dispatch('importProject', data);
    },

    importLayerArchive: async ({commit, dispatch, state}, data) => {
      console.log('importLayerArchive', data)
      await dispatch('importProject', data);
    },

    importLayer: async ({commit, dispatch, state}, data) => {
      console.log('importLayer', data)
      await dispatch('importProject', data);
    },

    importProject: ({commit, dispatch, state}, data) => {
      console.log('importProject', data)
      return new Promise(async (resolve, reject) => {
        await checkStoragePermissions();

        let newDocumentId;
        const file = data.data?.file?.get('archive') || data.data?.archive?.get('archive');
        const fileName = file.name;
        const filePath = `${config.APP_MOBILE_FOLDER}/temp/${fileName}`;

        const newFolderName = getFilenameWithoutExtension(file.name);
        let newFolderPath = `${config.APP_MOBILE_FOLDER}/${newFolderName}`;
        let unzippedPath = `${config.APP_MOBILE_FOLDER}`;

        if (data?.query?.parentDocumentId && await getDocumentById(data?.query.parentDocumentId)) {
          newFolderPath = `${config.APP_MOBILE_FOLDER}/${await getDocumentPath(data?.query?.parentDocumentId)}/Layers/${newFolderName}`;
          unzippedPath = `${config.APP_MOBILE_FOLDER}/${await getDocumentPath(data?.query?.parentDocumentId)}/Layers`;
        }

        try {
          await Filesystem.rmdir({
            path: newFolderPath,
            directory: Directory.Documents,
            recursive: true,
          });

          await Filesystem.mkdir({
            path: newFolderPath,
            directory: Directory.Documents,
            recursive: true,
          });
        } catch (e) {
        }

        // Ensure temp directory exists
        try {
          await Filesystem.mkdir({
            path: decodeURIComponent(`${config.APP_MOBILE_FOLDER}/temp`),
            directory: Directory.Documents,
            recursive: true,
          });
        } catch (e) {}

        const newFolderUri = await Filesystem.getUri({
          path: decodeURIComponent(newFolderPath),
          directory: Directory.Documents,
        });

        const unzippedUri = await Filesystem.getUri({
          path: decodeURIComponent(unzippedPath),
          directory: Directory.Documents,
        });

        // Write the file in chunks to avoid OOM when handling large files
        const CHUNK_SIZE = 1024 * 1024 * 2; // 2MB per chunk
        const readSliceAsBase64 = (blob) => new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => {
            const result = fr.result || '';
            const base64 = typeof result === 'string' && result.includes(',') ? result.split(',')[1] : result;
            resolve(base64);
          };
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });

        // Delete previous temp file if exists
        try {
          await Filesystem.deleteFile({
            path: decodeURIComponent(filePath),
            directory: Directory.Documents,
          });
        } catch (e) {}

        let offset = 0;
        while (offset < file.size) {
          const slice = file.slice(offset, Math.min(offset + CHUNK_SIZE, file.size));
          const base64Chunk = await readSliceAsBase64(slice);
          if (offset === 0) {
            await Filesystem.writeFile({
              path: decodeURIComponent(filePath),
              data: base64Chunk,
              directory: Directory.Documents,
              recursive: true,
            });
          } else {
            await Filesystem.appendFile({
              path: decodeURIComponent(filePath),
              data: base64Chunk,
              directory: Directory.Documents,
            });
          }
          offset += CHUNK_SIZE;
        }

        const importedFile = await Filesystem.getUri({
          path: decodeURIComponent(filePath),
          directory: Directory.Documents,
        });

        // unzip here
        try {
          await Zip.unzip({
            source: decodeURIComponent(importedFile.uri),
            destination: decodeURIComponent(unzippedUri.uri)
          });

          newDocumentId = await importDocumentFromFolder(decodeURIComponent(newFolderUri.uri), data.query);
        } catch (e) {
          reject(e);
        }

        // cleanup: delete temp folder
        await Filesystem.rmdir({
          path: decodeURIComponent(`${config.APP_MOBILE_FOLDER}/temp`),
          directory: Directory.Documents,
          recursive: true
        });
        const newDocType = await getDocumentTypeById(newDocumentId);
        const newDoc = await getDocumentById(newDocumentId);

        resolve(newDocType === 'layer' ? [newDoc] : newDoc);
      });
    },

    updateGeometry: ({dispatch, state}, data) => {
      console.log('updateGeometry', data);
      return new Promise((resolve, reject) => {
        db.geometries.update(data.data.geometryId, {
          ...prepareForSave(data.data)
        }).then(async result => {
          resolve(prepareForUse(await db.geometries.get(data.data.geometryId)));
        }).catch((error) => {
          throwError(error, dispatch, resolve, reject);
        });
      });
    },

    deleteGeometry: ({dispatch, state}, data) => {
      console.log('deleteGeometry', data);
      return new Promise((resolve, reject) => {
        db.geometries.where('geometryId').anyOf(data.data.geometryIds.map(Number)).delete();
        resolve({message: 'Geometria a fost stearsa.'});
      });
    },

    splitGeometries: ({dispatch, state}, data) => {
      console.log('splitGeometries', data);
      return new Promise((resolve, reject) => {
        reject('Not available in offline mode!');
      });
    },

    getFontsList: ({dispatch, state, commit}, data) => {
      console.log('getFontsList', data);
      return new Promise((resolve, reject) => {
        resolve([]);
      });
    },

    identifyPlant: ({dispatch, state}, data) => {
      return new Promise(async (resolve, reject) => {
        const headers = {
          "Content-Type": "application/json"
        };

        const imageAsBase64 = await imageToBase64(data?.data?.imageUrl);
        const url = `${config.API_URL}plant-identifier/identifyBase64`;
        const options = {
          method: "POST",
          headers: headers,
          body: JSON.stringify({
            image: imageAsBase64.replace("data:image/jpeg;base64,", "")
          })
        };

        fetch(url, options)
          .then(async (response) => {
            if (!response.ok) {
              throw await response.json(); // Parse error response
            }
            return response.json();
          })
          .then((responseData) => {
            resolve(responseData);
          })
          .catch((error) => {
            throwError(error, dispatch, resolve, reject);
          });
      });
    }

  },
  plugins: [localforagePlugin.plugin, localstoragePlugin.plugin],
});
export default store;
